import React, { useState } from 'react';
import { useExpenses, useDeleteExpense, Expense } from '../hooks/useExpenses.ts';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../utils/axios.ts';

function formatCurrency(amount) {
  return `KSh ${Number(amount).toLocaleString()}`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB');
}

function downloadReceipt(expense: Expense) {
  api.get(`/expenses/${expense.id}/download`, { responseType: 'blob' })
    .then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${expense.date}_${expense.category}_${expense.id}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(() => {
      alert('Failed to download receipt.');
    });
}

const ExpenseList = ({ onEdit, filters = {} }: { onEdit: (expense: Expense) => void, filters?: any }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useExpenses({ page, ...filters });
  const deleteExpense = useDeleteExpense();

  // Defensive: handle both paginated and non-paginated responses
  const expenses = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  const meta = data?.meta;

  if (isLoading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (error) {
    console.error(error);
    return (
      <div className="py-8 text-center text-red-500">
        Something went wrong. <button onClick={() => refetch()} className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded">Retry</button>
      </div>
    );
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="responsive-table min-w-full border rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Date</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Category</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Description</th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700 border-b">Amount</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700 border-b">Receipt</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense: Expense, idx: number) => (
              <tr
                key={expense.id}
                className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td data-label="Date" className="px-4 py-2 border-b">{formatDate(expense.date)}</td>
                <td data-label="Category" className="px-4 py-2 border-b">{expense.category}</td>
                <td data-label="Description" className="px-4 py-2 border-b">{expense.description}</td>
                <td data-label="Amount" className="px-4 py-2 border-b text-right">{formatCurrency(expense.amount)}</td>
                <td data-label="Receipt" className="px-4 py-2 border-b text-center">
                  {expense.receipt_path?.length > 0 ? (
                    <button
                      onClick={() => downloadReceipt(expense)}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      Download
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td data-label="Actions" className="px-4 py-2 border-b text-center">
                  <button
                    className="inline-flex items-center p-1 text-blue-600 hover:bg-blue-50 rounded mr-2"
                    title="Edit"
                    onClick={() => onEdit(expense)}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    className="inline-flex items-center p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this expense?')) {
                        deleteExpense.mutate(expense.id);
                      }
                    }}
                    disabled={deleteExpense.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {meta && meta.last_page > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(meta.from)} to {(meta.to)} of {meta.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={meta.current_page === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(prev => Math.min(meta.last_page, prev + 1))}
              disabled={meta.current_page === meta.last_page}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpenseList; 